import * as React from 'react';
import { cn } from '@/lib/utils';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'circular' | 'rectangular' | 'kpi' | 'chart';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'shimmer';
}

function Skeleton({
  className,
  variant = 'rectangular',
  width,
  height,
  animation = 'pulse',
  ...props
}: SkeletonProps) {
  const variantStyles = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
    kpi: 'h-24 rounded-xl',
    chart: 'h-64 rounded-xl',
  };

  const animationStyles = {
    pulse: 'animate-pulse bg-slate-200',
    shimmer: 'bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] animate-shimmer',
  };

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        variantStyles[variant],
        animationStyles[animation],
        className
      )}
      style={{
        width: width || undefined,
        height: height || undefined,
      }}
      {...props}
    />
  );
}

function KPISkeletonCard() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <Skeleton variant="text" className="mb-2 h-3 w-24" />
      <Skeleton variant="text" className="mb-1 h-8 w-32" />
      <Skeleton variant="text" className="h-3 w-20" />
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <Skeleton variant="text" className="h-5 w-32" />
        <Skeleton variant="rectangular" className="h-8 w-24" />
      </div>
      <Skeleton variant="chart" />
    </div>
  );
}

function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-200 bg-slate-50 p-4">
        <div className="flex gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} variant="text" className="h-4 w-24" />
          ))}
        </div>
      </div>
      <div className="p-4">
        {[...Array(rows)].map((_, i) => (
          <div key={i} className="mb-3 flex gap-4">
            {[...Array(4)].map((_, j) => (
              <Skeleton key={j} variant="text" className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export { Skeleton, KPISkeletonCard, ChartSkeleton, TableSkeleton };
